/* import __COLOCATED_TEMPLATE__ from './voice-playground.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type FinVoiceService from 'embercom/services/fin-voice-service';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import { tracked } from '@glimmer/tracking';
import type { FinVoiceVadSettings } from 'embercom/objects/ai-agent/fin-voice/fin-voice-server-events';
import type { FinVoiceVoice } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-options';
import type { FinVoicePipeline } from 'embercom/objects/ai-agent/fin-voice/fin-voice-pipeline-options';
import type { FinVoiceRunbook } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type { FinVoiceVoiceModel } from 'embercom/objects/ai-agent/fin-voice/fin-voice-voice-model-options';
import type { FinVoiceVoiceLanguage } from 'embercom/objects/ai-agent/fin-voice/fin-voice-language-options';
import type { AudioDeviceSettings } from 'embercom/services/fin-voice-service';
import type { FinVoiceProcedure } from 'embercom/objects/ai-agent/fin-voice/fin-voice-runbook-options';
import type IntlService from 'ember-intl/services/intl';

export interface AiAgentVoicePlaygroundArgs {
  isSideMenuOpen?: boolean;
  isDocked?: boolean;
  canSwapConfigs: boolean;
  closeSideMenu?: () => void;
}

interface Signature {
  Args: AiAgentVoicePlaygroundArgs;
  Element: HTMLElement;
}
export default class AiAgentVoicePlayground extends Component<Signature> {
  @service declare finVoiceService: FinVoiceService;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare appService: AppService;
  @service declare intl: IntlService;

  @tracked canLoadConfig = false;
  @tracked isLoading = true;
  @tracked procedures: FinVoiceProcedure[] = [];

  readonly LOG_TAB = 'logs';
  readonly SETTINGS_TAB = 'settings';

  @tracked sideMenuContent: typeof this.LOG_TAB | typeof this.SETTINGS_TAB = this
    .canUseFinVoicePlaygroundDev
    ? this.LOG_TAB
    : this.SETTINGS_TAB;

  get isSettingsOpen() {
    return this.sideMenuContent === this.SETTINGS_TAB;
  }

  get isLogsVisible() {
    return this.sideMenuContent === this.LOG_TAB;
  }

  get canSwapConfigs() {
    return this.canLoadConfig && this.args.canSwapConfigs;
  }

  get isMuted() {
    return this.finVoiceService.isMuted;
  }

  get connectionState() {
    return this.finVoiceService.websocketState;
  }

  get isDisconnected() {
    return this.finVoiceService.websocketState === 'disconnected';
  }

  get sessions() {
    return this.finVoiceService.sessions;
  }

  get hasSessions() {
    return !!this.sessions?.length;
  }

  get selectedNumberConfigurationPhoneNumberId() {
    return this.finVoiceService.selectedNumberConfigurationPhoneNumber;
  }

  get selectedNumberConfigurationPhoneNumber() {
    if (!this.selectedNumberConfigurationPhoneNumberId) {
      return undefined;
    }

    return this.finVoiceConfigService.phoneNumbers[this.selectedNumberConfigurationPhoneNumberId]
      ?.phoneNumber;
  }

  get useSemanticVad() {
    return this.finVoiceService.useSemanticVad;
  }

  get semanticVadSettings() {
    return this.finVoiceService.semanticVadSettings;
  }

  get vadSettings() {
    return this.finVoiceService.vadSettings;
  }

  get selectedVoice() {
    return this.finVoiceService.selectedVoice;
  }

  get greeting() {
    return this.finVoiceService.greeting;
  }

  get selectedPipeline() {
    return this.finVoiceService.selectedPipeline;
  }

  get selectedRunbook() {
    return this.finVoiceService.selectedRunbook;
  }

  get selectedVoiceModel() {
    return this.finVoiceService.selectedVoiceModel;
  }

  get selectedLanguage() {
    return this.finVoiceService.selectedLanguage;
  }

  get audioDeviceSettings() {
    return this.finVoiceService.audioDeviceSettings;
  }

  get canUseFinVoicePlaygroundDev() {
    return this.appService.app.canUseFinVoicePlaygroundDev;
  }

  get verboseLogsEnabled() {
    return this.finVoiceService.verboseLogsEnabled;
  }

  get hasLoadedNumberConfigurations() {
    return !this.canLoadConfig || this.finVoiceConfigService.numberConfigurationsLoaded;
  }

  get hasConfigChanges() {
    if (!this.finVoiceService.selectedNumberConfigurationPhoneNumber) {
      return false;
    }

    let selectedNumberConfiguration =
      this.finVoiceConfigService.numberConfigurations[
        this.finVoiceService.selectedNumberConfigurationPhoneNumber
      ];

    if (!selectedNumberConfiguration) {
      return false;
    }

    return (
      this.finVoiceService.selectedVoice !== selectedNumberConfiguration.voice ||
      this.finVoiceService.greeting !== selectedNumberConfiguration.greeting
    );
  }

  get conversationPadding() {
    if (this.args.isDocked) {
      return 'px-6';
    }

    if (this.args.isSideMenuOpen) {
      return 'px-12';
    }

    return 'px-20';
  }

  constructor(owner: unknown, args: any) {
    super(owner, args);

    this.loadDataIfPermitted();
  }

  async loadDataIfPermitted() {
    try {
      this.canLoadConfig =
        await this.finVoiceConfigService.checkPermissionToAccessProductSettings(false);

      if (this.canLoadConfig) {
        await this.finVoiceConfigService.loadAllData();
      }

      this.procedures = await this.finVoiceService.fetchProcedures(this.appService.app.id);
    } catch (error) {
      console.error(error);
    } finally {
      this.isLoading = false;
    }
  }

  willDestroy() {
    super.willDestroy();
    this.disconnect();
  }

  @action
  selectConfig(phoneNumber: string | undefined) {
    this.finVoiceService.selectNumberConfiguration(phoneNumber);
  }

  @action
  resetConfig() {
    this.finVoiceService.resetNumberConfiguration();
  }

  @action
  async deployConfig() {
    await this.finVoiceService.deployNumberConfiguration();
  }

  @action
  connect() {
    return this.finVoiceService.connectStream();
  }

  @action
  disconnect() {
    return this.finVoiceService.disconnectStream();
  }

  @action closeSideMenu() {
    if (this.args.closeSideMenu) {
      this.args.closeSideMenu();
    }
  }

  @action
  mute() {
    return this.finVoiceService.muteAudio();
  }

  @action
  unmute() {
    return this.finVoiceService.unmuteAudio();
  }

  @action
  sendTextMessage(message: string) {
    return this.finVoiceService.sendTextMessage(message);
  }

  @action
  updateVadSetting(key: keyof FinVoiceVadSettings, value: number) {
    this.finVoiceService.vadSettings = {
      ...this.finVoiceService.vadSettings,
      [key]: value,
    };
  }

  @action
  updateAudioDeviceSetting(key: keyof AudioDeviceSettings, value: boolean) {
    this.finVoiceService.audioDeviceSettings = {
      ...this.finVoiceService.audioDeviceSettings,
      [key]: value,
    };
  }

  @action
  updateVoice(voice: FinVoiceVoice) {
    this.finVoiceService.selectedVoice = voice;
  }

  @action
  updateGreeting(greeting: string) {
    this.finVoiceService.greeting = greeting;
  }

  @action
  updatePipeline(pipeline: FinVoicePipeline) {
    this.finVoiceService.selectedPipeline = pipeline;
  }

  @action
  updateRunbook(runbook: FinVoiceRunbook) {
    this.finVoiceService.updateRunbook(runbook);
  }

  @action
  updateProcedure(procedure: FinVoiceProcedure) {
    this.finVoiceService.updateProcedure(procedure);
  }

  @action
  updateVoiceModel(voiceModel: FinVoiceVoiceModel) {
    this.finVoiceService.selectedVoiceModel = voiceModel;
  }

  @action
  updateLanguage(language: FinVoiceVoiceLanguage) {
    this.finVoiceService.updateLanguage(language);
  }

  @action
  clearAllSessions() {
    return this.finVoiceService.clearAllSessions();
  }

  @action
  getFrequencyData() {
    return this.finVoiceService.getFrequencyData();
  }

  @action
  updateUseSemanticVad(value: boolean): void {
    this.finVoiceService.updateUseSemanticVad(value);
  }

  @action
  updateEagerness(value: string): void {
    this.finVoiceService.updateEagerness(value);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::VoicePlayground': typeof AiAgentVoicePlayground;
  }
}
